import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { isOwnerOrFinance } from "../../utils/role-check";
import "./projects.style.css";
import StyledModalView from "../Modal/StyledModalView";
import api from "../../Api";
import JSZip from "jszip";
import { generateProjectPDF } from "../../utils/pdfGenerator";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";

function ProjectsList({
    projects,
    loading,
    error,
    userId,
    auth,
    onViewProject,
    onDeleteProject,
    onViewProjectTimesheets,
    isArchive = false,
    showFlash
}) {
    const { t } = useTranslation();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [search, setSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("name-asc"); // name-asc, name-desc, profit-desc, profit-asc, date-desc, date-asc

    const handleDeleteClick = (project) => {
        setSelectedProject(project);
        setShowDeleteModal(true);
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setSelectedProject(null);
    };

    const handleConfirmDelete = () => {
        if (selectedProject) {
            onDeleteProject(selectedProject.id);
        }
        handleCloseModal();
    };

    const handleExportZip = async () => {
        if (!selectedProject) return;
        try {
            // 1. Fetch the zip from backend
            const response = await api.get(`/projects/${selectedProject.id}/export-zip`, {
                responseType: 'blob',
                headers: { 'Accept': 'application/zip' },
            });
            const backendZipBlob = response.data;
            // 2. Generate the project info PDF (in memory, without auto-downloading)
            const projectPdfDoc = await generateProjectPDF(selectedProject, false);
            // Get PDF as Uint8Array (for JSZip)
            const projectPdfArrayBuffer = projectPdfDoc.output('arraybuffer');
            // 3. Unzip backend zip, add PDF, re-zip
            const zip = new JSZip();
            let backendZip;
            try {
                backendZip = await JSZip.loadAsync(backendZipBlob);
                backendZip.forEach((relativePath, file) => {
                    zip.file(relativePath, file.async('blob'));
                });
            } catch (zipErr) {
                // If not a valid zip, just create a new one
            }
            zip.file(`project-${selectedProject.name}-informatie.pdf`, projectPdfArrayBuffer);
            const finalZipBlob = await zip.generateAsync({ type: 'blob' });
            // 4. Download
            const url = window.URL.createObjectURL(finalZipBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${selectedProject.name || 'project'}-files.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showFlash(t('errors.exportFailed') + ": " + (err.response?.data?.error || err.message), "error");
        }
    };

    if (loading) {
        return <Loader message={t('common.loading')} />;
    }
    if (error) {
        return <NoDataConstruction text={t('common.error')}/>;
    }

    // Apply search filter
    const filteredProjects = projects.filter(project => {
        const searchLower = search.toLowerCase();
        return (
            project.name?.toLowerCase().includes(searchLower) ||
            project.client?.toLowerCase().includes(searchLower) ||
            project.location?.toLowerCase().includes(searchLower) ||
            project.address?.toLowerCase().includes(searchLower) ||
            String(project.id).includes(searchLower)
        );
    });

    // Apply sorting
    const sortedProjects = [...filteredProjects].sort((a, b) => {
        switch (sortOrder) {
            case "name-asc":
                return (a.name || "").localeCompare(b.name || "");
            case "name-desc":
                return (b.name || "").localeCompare(a.name || "");
            case "profit-desc":
                return (b.profit || 0) - (a.profit || 0);
            case "profit-asc":
                return (a.profit || 0) - (b.profit || 0);
            case "date-desc":
                return new Date(b.start_date || 0) - new Date(a.start_date || 0);
            case "date-asc":
                return new Date(a.start_date || 0) - new Date(b.start_date || 0);
            default:
                return 0;
        }
    });

    // Calculate total profit for filtered projects
    const filteredTotalProfit = sortedProjects.reduce((sum, p) => sum + (p.profit || 0), 0);

    return (
        <>
            {/* Search and Filter Bar - Using Centralized Styling */}
            <section className="filters-bar" aria-label="Project filters">
                <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                        id="project-search"
                        className="search-input"
                        type="search"
                        placeholder={t('projects.searchPlaceholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        aria-label={t('projects.searchPlaceholder')}
                    />
                    {search && (
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={() => setSearch('')}
                            aria-label="Wis zoekopdracht"
                        >
                            ✕
                        </button>
                    )}
                </div>
                <div className="filters-right">
                    <div className="filter-group">
                        <label htmlFor="sort-select">
                            {t('common.sortBy')}
                        </label>
                        <select
                            id="sort-select"
                            className="filter-select"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            aria-label={t('common.sortBy')}
                        >
                            <option value="name-asc">{t('projects.sortNameAsc')}</option>
                            <option value="name-desc">{t('projects.sortNameDesc')}</option>
                            {isOwnerOrFinance(auth.role) && !isArchive && (
                                <>
                                    <option value="profit-desc">{t('projects.sortProfitDesc')}</option>
                                    <option value="profit-asc">{t('projects.sortProfitAsc')}</option>
                                </>
                            )}
                            <option value="date-desc">{t('projects.sortDateDesc')}</option>
                            <option value="date-asc">{t('projects.sortDateAsc')}</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* Results count indicator */}
            {sortedProjects.length > 0 && (
                <div className="results-summary mb-4 p-3 rounded" role="status" aria-live="polite">
                    <span className="font-semibold">
                        {sortedProjects.length} {sortedProjects.length === 1 ? 'project' : 'projecten'}
                    </span>
                    {search && (
                        <span className="text-muted ml-2">
                            (gefilterd van {projects.length})
                        </span>
                    )}
                </div>
            )}

            {sortedProjects.length === 0 ? (
                <div className="text-center w-full p-8" role="status">
                    {search ? (
                        <NoDataConstruction text={t('projects.noProjectsFoundFor', { search })}/>
                    ) : isArchive ? (
                        <NoDataConstruction text={t('projects.noArchivedProjects')}/>
                    ) : (
                        <NoDataConstruction text={t('projects.noProjects')}/>
                    )}
                </div>
            ) : (
                <div className="project-info-grid enhanced-mobile-grid card-grid">
                    {sortedProjects.map((project) => (
                        <article
                            className={`project-card card card-elevated${isArchive ? " archived" : ""}`}
                            key={project.id}
                        >
                            <header className="project-card-header card-header">
                                <div className="flex items-start justify-between w-full">
                                    <h3 className="card-title text-xl mb-0">{project.name}</h3>
                                    {isArchive && (
                                        <span className="archive-badge badge badge-secondary" aria-label="Gearchiveerd project">
                                            {t('projects.archived')}
                                        </span>
                                    )}
                                </div>
                                <div className="project-id-badge text-sm text-muted mt-2">
                                    #{project.id}-{project.start_date ? new Date(project.start_date).getFullYear() : 'N/A'}
                                </div>
                            </header>

                            <div className="project-card-body card-body">
                                <dl className="metadata-list">
                                    <div className="metadata-item">
                                        <dt className="metadata-label">
                                            <span className="font-semibold">{t('projects.client')}</span>
                                        </dt>
                                        <dd className="metadata-value">
                                            {project.client || <span className="placeholder text-muted italic">{t('common.unknown')}</span>}
                                        </dd>
                                    </div>

                                    <div className="metadata-item">
                                        <dt className="metadata-label">
                                            <span className="font-semibold">{t('projects.location')}</span>
                                        </dt>
                                        <dd className="metadata-value">
                                            {project.location || <span className="placeholder text-muted italic">{t('common.unknown')}</span>}
                                        </dd>
                                    </div>

                                    {project.address && (
                                        <div className="metadata-item">
                                            <dt className="metadata-label">
                                                <span className="font-semibold">{t('projects.address')}</span>
                                            </dt>
                                            <dd className="metadata-value">
                                                {project.address}
                                            </dd>
                                        </div>
                                    )}
                                </dl>

                                <div className="date-badges-container">
                                    {project.start_date && (
                                        <div className="date-badge">
                                            <time dateTime={project.start_date} className="text-sm">
                                                {new Date(project.start_date).toLocaleDateString('nl-NL', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </time>
                                        </div>
                                    )}
                                    {project.start_date && project.end_date && (
                                        <span className="date-separator" aria-hidden="true">→</span>
                                    )}
                                    {project.end_date && (
                                        <div className="date-badge">
                                            <time dateTime={project.end_date} className="text-sm">
                                                {new Date(project.end_date).toLocaleDateString('nl-NL', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </time>
                                        </div>
                                    )}
                                </div>

                                {isOwnerOrFinance(auth.role) && !isArchive && (
                                    <div className={`value-display ${
                                        project.profit === null
                                            ? "value-loading"
                                            : project.profit >= 0 ? "value-positive" : "value-negative"
                                    }`}>
                                        <div className="value-label">
                                            {project.profit === null
                                                ? t('projects.profit')
                                                : project.profit >= 0 ? t('projects.profit') : t('projects.loss')
                                            }
                                        </div>
                                        <span className="value-amount">
                                            {project.profit === null
                                                ? '...'
                                                : `€ ${project.profit.toLocaleString("nl-NL", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}`}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <footer className="card-actions card-footer">
                                <button
                                    type="button"
                                    className="btn btn-view flex-1"
                                    onClick={() => onViewProject(project.id)}
                                    aria-label={`${t('projects.viewDetails')} ${project.name}`}
                                >
                                    {t('projects.details')}
                                </button>
                                {!userId && !isArchive && (
                                    <button
                                        type="button"
                                        className="btn btn-edit flex-1"
                                        onClick={() => onViewProjectTimesheets && onViewProjectTimesheets(project.id)}
                                        aria-label={`${t('projects.viewTimesheets')} ${project.name}`}
                                    >
                                        {t('timesheets.title')}
                                    </button>
                                )}
                                {isOwnerOrFinance(auth.role) && (
                                    <button
                                        type="button"
                                        className="btn btn-delete"
                                        onClick={() => handleDeleteClick(project)}
                                        aria-label={`${t('common.delete')} ${project.name}`}
                                    >
                                        {t('common.delete')}
                                    </button>
                                )}
                            </footer>
                        </article>
                    ))}
                </div>
            )}

            {!isArchive && isOwnerOrFinance(auth.role) && sortedProjects.length > 0 && (
                <aside className="totals-overview card card-elevated mt-6" aria-label="Project totals">
                    <div className="card-header">
                        <h3 className="card-title text-lg">
                            Totaal Overzicht
                        </h3>
                    </div>
                    <div className="card-body">
                        <div className="totals-grid">
                            <div className="total-item">
                                <div className="total-label">
                                    {t('projects.totalProjects')}
                                </div>
                                <div className="total-value">
                                    {sortedProjects.length}
                                </div>
                                {search && (
                                    <div className="total-meta">
                                        van {projects.length} totaal
                                    </div>
                                )}
                            </div>

                            <div className="total-divider"></div>

                            <div className="total-item">
                                <div className="total-label">
                                    {t('projects.total')} {filteredTotalProfit >= 0 ? t('projects.profit') : t('projects.loss')}
                                </div>
                                <div className={`total-value ${filteredTotalProfit >= 0 ? "text-success" : "text-error"}`}>
                                    € {filteredTotalProfit.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                {sortedProjects.length > 0 && (
                                    <div className="total-meta">
                                        Ø € {(filteredTotalProfit / sortedProjects.length).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per project
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </aside>
            )}

            {/* Delete confirmation modal */}
            <StyledModalView
                isOpen={showDeleteModal && !!selectedProject}
                onClose={handleCloseModal}
                title={t('projects.deleteProject')}
                confirmText={t('common.delete')}
                closeText={t('common.cancel')}
                onConfirm={handleConfirmDelete}
            >
                <p className="mb-4">{t('projects.confirmDelete')}: "{selectedProject?.name}"?</p>
                <button
                    type="button"
                    className="btn btn-submit mb-3"
                    onClick={handleExportZip}
                    aria-label={`${t('projects.exportZip')} voor ${selectedProject?.name}`}
                >
                    {t('projects.exportZip')}
                </button>
            </StyledModalView>
        </>
    );
}

ProjectsList.propTypes = {
    projects: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    userId: PropTypes.number,
    auth: PropTypes.object,
    onViewProject: PropTypes.func.isRequired,
    onDeleteProject: PropTypes.func.isRequired,
    onViewProjectTimesheets: PropTypes.func.isRequired,
    isArchive: PropTypes.bool,
    showFlash: PropTypes.func.isRequired,
};

export default ProjectsList;
